// PostFeed.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import objectifyArrayById from '../resource/objectifyArrayById.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import SuggestedPostFeed from '../container/SuggestedPostFeed.js';
import Post from '../container/Post.js';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import Loading from '../component/Loading.jsx';

import { textColor } from '../style/variables.js';
import media from '../style/media.js';

import UserNoMorePostIconSrouce from '../../img/img_empty_no_more_post.svg';

export class PostFeed extends React.PureComponent {
  resizeObserver = null;

  fetchFeeds = ({ page, shouldSkipLocalCache = false }) => {
    const { category, fetchFeeds } = this.props;
    return fetchFeeds({
      page,
      type: category,
      shouldSkipLocalCache,
    });
  };

  renderLoading = () => {
    return (
      <LoadingWrapper>
        <Loading size={24} />
      </LoadingWrapper>
    );
  };

  renderSuggestedList = ({ isAllPostViewed } = {}) => {
    const { t } = this.props;
    return (
      <>
        {isAllPostViewed && (
          <Empty isAllPostViewed={isAllPostViewed}>
            <EmptyImage src={UserNoMorePostIconSrouce} />
            <EmptyText>
              {t('posts_be_viewed', {
                ns: TranslationNamespace.FEED,
              })}
            </EmptyText>
          </Empty>
        )}
        <SuggestedPostList>
          <SuggestedListHeader>
            {t('suggested_posts', {
              ns: TranslationNamespace.FEED,
            })}
          </SuggestedListHeader>
          <SuggestedPostFeed />
        </SuggestedPostList>
      </>
    );
  };

  renderList = () => {
    const {
      category,
      nextPage,
      postIds,
      currentAutoplayPostId,
      isNextPageFetching,
      prefetchedMessageAmount,
    } = this.props;
    const currentAutoplayPostIndex = postIds.indexOf(currentAutoplayPostId);

    return (
      <>
        <InfiniteScroller
          pageStart={1}
          hasMore={nextPage != null}
          loadMore={() => this.fetchFeeds({ page: nextPage })}
          isFetching={isNextPageFetching}
        >
          <Posts>
            <ScrollItem
              loader={
                <PostWrapper>
                  <Loading size={24} />
                </PostWrapper>
              }
            >
              {postIds.map((messageId, postIndex) => {
                return (
                  <PostWrapper
                    key={messageId}
                    data-message_id={messageId}
                    ref={element => {
                      if (element && this.resizeObserver) {
                        this.resizeObserver.unobserve(element);
                        this.resizeObserver.observe(element);
                      }
                    }}
                  >
                    <Post
                      messageId={messageId}
                      shouldPrefetch={
                        // If there is any post has been intersected (currentAutoplayPostIndex >= 0),
                        // prefetch the segments for intersected one and the next two (if any)
                        currentAutoplayPostIndex >= 0 &&
                        postIndex >= currentAutoplayPostIndex &&
                        postIndex <=
                          currentAutoplayPostIndex + prefetchedMessageAmount
                      }
                      shouldShowSubscribeAndUnlock
                      isOnPostFeed
                      messageHeightFeedName={category}
                    />
                  </PostWrapper>
                );
              })}
            </ScrollItem>
          </Posts>
        </InfiniteScroller>
        {nextPage == null &&
          this.renderSuggestedList({ isAllPostViewed: true })}
      </>
    );
  };

  componentDidMount() {
    const { category, setMessageHeightInFeed } = this.props;
    this.timeout = setTimeout(() => {
      this.fetchFeeds({ page: 1 });
    });
    this.resizeObserver = new ResizeObserver(entries => {
      const messageObject = objectifyArrayById({
        array: entries.map(entry => ({
          id: entry.target.getAttribute('data-message_id'),
          messageHeight: entry.target.clientHeight,
        })),
      });
      return setMessageHeightInFeed({
        feedName: category,
        messageObject,
      });
    });
  }

  componentDidUpdate(prevProps) {
    const { meId, shouldRefresh } = this.props;
    if (
      meId !== prevProps.meId ||
      (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh)
    ) {
      this.fetchFeeds({ page: 1, shouldSkipLocalCache: true });
    }
  }

  componentWillUnmount() {
    this.timeout && clearTimeout(this.timeout);
    this.resizeObserver?.disconnect();
  }

  render() {
    const { isFirstPageFetching, isFirstPageFetched, postIds, titleI18nId, t } =
      this.props;
    return (
      <StyledPostFeed>
        {isFirstPageFetched && !postIds.length ? null : (
          <Header>
            <Title>
              {t(titleI18nId, {
                ns: TranslationNamespace.FEED,
              })}
            </Title>
          </Header>
        )}
        {isFirstPageFetching
          ? this.renderLoading()
          : postIds.length === 0
            ? this.renderSuggestedList()
            : this.renderList()}
      </StyledPostFeed>
    );
  }
}

PostFeed.propTypes = {
  t: PropTypes.func.isRequired,
  meId: PropTypes.string,
  category: PropTypes.string,
  titleI18nId: PropTypes.string,
  currentAutoplayPostId: PropTypes.string,
  postIds: PropTypes.array,
  nextPage: PropTypes.number,
  prefetchedMessageAmount: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  fetchFeeds: PropTypes.func,
  setMessageHeightInFeed: PropTypes.func,
};

PostFeed.defaultProps = {
  meId: null,
  category: null,
  titleI18nId: null,
  currentAutoplayPostId: '',
  postIds: [],
  nextPage: null,
  prefetchedMessageAmount: 2,
  isNextPageFetching: false,
  isFirstPageFetching: false,
  isFirstPageFetched: false,
  shouldRefresh: false,
  fetchFeeds: () => null,
  setMessageHeightInFeed: () => null,
};

const StyledPostFeed = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 680px;
`;

const Header = styled.div`
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.tablet`
    font-size: 18px;
  `}
`;

const Posts = styled.div``;

const PostWrapper = styled.div``;

const LoadingWrapper = styled.div`
  margin-top: 60px;
  display: flex;
  justify-content: center;
`;

const Empty = styled.div`
  margin-top: 56px;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EmptyImage = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: ${textColor.black};
  text-align: center;
`;

const SuggestedPostList = styled.div``;

const SuggestedListHeader = styled.div`
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  padding: 0px 16px;
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${textColor.white};
`;

export default withTranslation()(PostFeed);
